import React from 'react';

/**
 * Path data for the 3x3 pill grid used in transition frames.
 * Order: top-right, mid-left, top-center, mid-center, bot-left, top-left, bot-right, mid-right, bot-center
 */
const PILL_PATHS = [
  'M296 0.00194472C309.255 0.00194318 320 10.7471 320 24.0019C320 37.2566 309.255 48.0018 296 48.0018L248 48.0018C234.745 48.0018 224 37.2566 224 24.0019C224 10.7471 234.745 0.00195187 248 0.00195032L296 0.00194472Z',
  'M72 68.0005C85.2548 68.0005 96 78.7456 96 92.0004C96 105.255 85.2548 116 72 116L24 116C10.7452 116 -9.7199e-06 105.255 -6.26167e-06 92.0004C-2.80345e-06 78.7456 10.7452 68.0005 24 68.0005L72 68.0005Z',
  'M136 48.0005C122.745 48.0005 112 37.2554 112 24.0006C112 10.7458 122.745 0.000675364 136 0.0006743L184 0.000670451C197.255 0.000669388 208 10.7458 208 24.0006C208 37.2554 197.255 48.0005 184 48.0005L136 48.0005Z',
  'M136 115.999C122.745 115.999 112 105.254 112 91.9991C112 78.7443 122.745 67.9992 136 67.9992L184 67.9992C197.255 67.9992 208 78.7443 208 91.9991C208 105.254 197.255 115.999 184 115.999L136 115.999Z',
  'M72 136.001C85.2548 136.001 96 146.746 96 160.001C96 173.256 85.2548 184.001 72 184.001L24 184.001C10.7452 184.001 -2.15433e-06 173.256 -1.38784e-06 160.001C-6.21358e-07 146.746 10.7452 136.001 24 136.001L72 136.001Z',
  'M72 0.00146712C85.2548 0.00146754 96 10.7466 96 24.0014C96 37.2562 85.2548 48.0013 72 48.0013L24 48.0013C10.7452 48.0013 -2.14811e-06 37.2562 -1.38384e-06 24.0014C-6.19565e-07 10.7466 10.7452 0.00146518 24 0.0014656L72 0.00146712Z',
  'M248.001 184C234.746 184 224.001 173.254 224.001 160C224.001 146.745 234.746 136 248.001 136L296.001 136C309.256 136 320.001 146.745 320.001 160C320.001 173.254 309.256 184 296.001 184L248.001 184Z',
  'M296 68.0005C309.255 68.0005 320 78.7456 320 92.0004C320 105.255 309.255 116 296 116L248 116C234.745 116 224 105.255 224 92.0004C224 78.7456 234.745 68.0005 248 68.0005L296 68.0005Z',
  'M136 184C122.745 184 112 173.254 112 160C112 146.745 122.745 136 136 136L184 136C197.255 136 208 146.745 208 160C208 173.254 197.255 184 184 184L136 184Z'
];

/** Stroke-based paths for the initial (small) logo icon */
const STROKE_PATHS = [
  { d: 'M62 6L74 6' },
  { d: 'M6 23L18 23' },
  { d: 'M46 6L34 6' },
  { d: 'M46 23L34 23' },
  { d: 'M6 40L18 40' },
  { d: 'M6 6L18 6' },
  { d: 'M74 40L62 40' },
  { d: 'M62 23L74 23' },
  { d: 'M46 40L34 40' }
];

// Color palettes: index 0 = initial (colorful), 1-7 = transition frames, 8 = final (embedded)
export const LOGO_PALETTES: string[][] = [
  ['#F6D4D5', '#E79758', '#E4736B', '#80C1A1', '#79476A', '#F4C963', '#009BB7', '#B1D6DF', '#006F6F'], // initial
  ['#EACCCD', '#DD9760', '#DB7770', '#80C1A1', '#7D516F', '#E9C369', '#139AB3', '#AECED6', '#137474'], // frame 1
  ['#DFC4C5', '#D39767', '#D17C76', '#80C1A1', '#815B75', '#DDBC70', '#269AAF', '#ABC6CD', '#267979'], // frame 2
  ['#D3BCBD', '#C9966F', '#C8807B', '#80C1A1', '#85647A', '#D2B676', '#3999AA', '#A8BEC3', '#397D7D'], // frame 3
  ['#C7B5B5', '#C09677', '#BE8480', '#80C1A1', '#896E80', '#C6AF7C', '#4C98A6', '#A5B6BA', '#4C8282'], // frame 4
  ['#BBADAD', '#B6967E', '#B58885', '#80C1A1', '#8C7885', '#BBA982', '#5F97A2', '#A1ADB1', '#5F8787'], // frame 5
  ['#B0A5A5', '#AC9686', '#AB8D8B', '#80C1A1', '#90828A', '#AFA289', '#72979E', '#9EA5A8', '#728C8C'], // frame 6
  ['#A49D9D', '#A2958D', '#A29190', '#80C1A1', '#948B90', '#A49C8F', '#859699', '#9B9D9E', '#859090'], // frame 7
  ['#989595', '#989595', '#989595', '#80C1A1', '#989595', '#989595', '#989595', '#989595', '#989595'] // embedded (final)
];

interface LogoIconProps {
  paletteIndex: number;
  className?: string;
}

/**
 * Renders the FinSuite 3x3 pill grid logo.
 * Uses stroke-based paths for the initial state (index 0) and
 * fill-based paths for transition frames (index 1-8).
 */
export const LogoIcon: React.FC<LogoIconProps> = ({ paletteIndex, className }) => {
  const colors = LOGO_PALETTES[paletteIndex] ?? LOGO_PALETTES[0];

  if (paletteIndex === 0) {
    return (
      <svg className={className} viewBox="0 0 80 46" fill="none" xmlns="http://www.w3.org/2000/svg">
        {STROKE_PATHS.map((p, i) => (
          <path key={i} d={p.d} stroke={colors[i]} strokeWidth="12" strokeLinecap="round" />
        ))}
      </svg>
    );
  }

  return (
    <svg className={className} viewBox="0 0 320 184" fill="none" xmlns="http://www.w3.org/2000/svg">
      {PILL_PATHS.map((d, i) => (
        <path key={i} d={d} fill={colors[i]} />
      ))}
    </svg>
  );
};
